import { PerfilCaso } from '../components/PerfilCaso';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { BrandSwitcher } from '../components/BrandSwitcher';
import { VozLlamada } from '../components/VozLlamada';
import { listLeads, updateLead, ensureDemoLead, resetLeads, ESTADOS } from '../engine/leads';
import { agenteCatalogo, agenteComparador, agenteCumplimiento, fichaLlamada, CATALOGO_TOTAL } from '../engine/agentes';
import { MARCAS } from '../engine/marcas';
import { pdfFichaLlamada } from '../engine/pdf';
import { IconSearch, IconCheck, IconAdvisor, IconShield, IconDownload, IconChat, IconPhone } from '../components/icons';

const AGENTES = [
  { id: 'catalogo', nombre: 'Agente de catálogo', desc: 'Filtra el catálogo por la necesidad del lead' },
  { id: 'comparador', nombre: 'Agente comparador', desc: 'Cruza las opciones lado a lado' },
  { id: 'cumplimiento', nombre: 'Agente de cumplimiento', desc: 'Marca alertas y verifica que nada sea recomendación' },
];

const estadoClase = (e) => ({ 'Nuevo': 'nuevo', 'En análisis': 'analisis', 'Listo para llamar': 'listo', 'Llamado': 'llamado' }[e] || 'nuevo');

export default function Corredor({ marca }) {
  const [version, setVersion] = useState(0);
  const leadsAll = useMemo(() => listLeads(), [version]);
  // La bandeja muestra solo los leads de la entidad activa (igual que el panel, para que cuadren).
  const leads = useMemo(() => leadsAll.filter((l) => (l.marca || 'mbm') === marca.id), [leadsAll, marca.id]);
  const [abierto, setAbierto] = useState(null); // lead id
  const [fase, setFase] = useState('idle'); // idle · corriendo · listo
  const [activo, setActivo] = useState(-1);
  const [resultado, setResultado] = useState(null);
  const [llamada, setLlamada] = useState(null);
  const [destacado, setDestacado] = useState(null);
  const timers = useRef([]);

  // Demo guiada (#corredor&demo): reinicia el lead demo y lo siembra arriba, con la marca activa.
  useEffect(() => {
    if (!window.location.hash.includes('demo')) return;
    const d = ensureDemoLead(marca.id, true);
    setVersion((v) => v + 1);
    setAbierto(d.id);
    setFase('idle');
    setActivo(-1);
    setResultado(null);
    setDestacado(d.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reiniciarBandeja = () => {
    resetLeads();
    setAbierto(null);
    setFase('idle');
    setActivo(-1);
    setResultado(null);
    setDestacado(null);
    setVersion((v) => v + 1);
  };

  const lead = leads.find((l) => l.id === abierto) || null;

  // Panel de entidad: leads de la marca activa, conteo por estado y conversión.
  const panel = useMemo(() => {
    const dela = leads;
    const por = ESTADOS.reduce((a, e) => ({ ...a, [e]: dela.filter((l) => l.estado === e).length }), {});
    const conv = dela.length ? Math.round((por['Llamado'] / dela.length) * 100) : 0;
    return { total: dela.length, por, conv };
  }, [leads]);

  const abrir = (l) => {
    timers.current.forEach(clearTimeout);
    setAbierto(l.id);
    setFase('idle');
    setActivo(-1);
    setResultado(null);
  };

  const buscarComparar = () => {
    if (!lead) return;
    setFase('corriendo');
    setActivo(0);
    updateLead(lead.id, { estado: 'En análisis' });
    const cat = agenteCatalogo(lead);
    const comp = agenteComparador(cat.productos);
    const alertas = agenteCumplimiento(lead, cat.productos);
    const ficha = fichaLlamada(lead, cat.productos);
    // Los tres agentes trabajan en secuencia, con su tiempo.
    const t1 = setTimeout(() => setActivo(1), 1400);
    const t2 = setTimeout(() => setActivo(2), 3000);
    const t3 = setTimeout(() => {
      setResultado({ cat, comp, alertas, ficha });
      setActivo(3);
      setFase('listo');
      updateLead(lead.id, { estado: 'Listo para llamar' });
      setVersion((v) => v + 1);
    }, 4600);
    timers.current = [t1, t2, t3];
  };

  const llamar = () => {
    if (!lead) return;
    setLlamada(lead);
  };

  const cerrarLlamada = (transcript, modo) => {
    // Solo una llamada real marca "Llamado" y guarda la transcripción; el ejemplo no altera el lead.
    if (modo !== 'simulado' && lead && transcript && transcript.length) {
      updateLead(lead.id, {
        estado: 'Llamado',
        transcripcion: transcript.map((l) => `${l.src === 'ai' ? marca.nombre : lead.nombre}: ${l.t}`).join('\n'),
      });
    }
    setLlamada(null);
    setVersion((v) => v + 1);
  };

  // Ficha compartible: descarga el PDF (para adjuntar) y abre WhatsApp con un mensaje de la entidad.
  const enviarWhatsApp = () => {
    if (!lead) return;
    pdfFichaLlamada(lead, marca, resultado);
    const num = (lead.whatsapp || '').replace(/\D/g, '');
    const tel = num.startsWith('57') ? num : `57${num}`;
    const msg = encodeURIComponent(
      `Hola ${lead.nombre}, soy tu asesor de ${marca.nombre}. Te comparto la ficha de tu consulta (${lead.necesidad}); te adjunto el PDF que acabo de descargar.`,
    );
    window.open(`https://wa.me/${tel}?text=${msg}`, '_blank', 'noopener');
  };

  return (
    <div className="corredor" style={{ '--marca': marca.color }} data-testid="corredor-page">
      <BrandSwitcher marca={marca} vista="corredor" />

      <div className="corredor-grid">
        {/* Bandeja de leads */}
        <aside className="corr-inbox" data-testid="corr-inbox">
          <div className="corr-panel" data-testid="corr-panel">
            <h2 className="corr-h2">Panel · {marca.nombre}</h2>
            <div className="corr-panel-kpis">
              <div className="corr-kpi"><b data-testid="panel-total">{panel.total}</b><span>leads</span></div>
              <div className="corr-kpi"><b data-testid="panel-conv" style={{ color: marca.color }}>{panel.conv}%</b><span>convertidos</span></div>
            </div>
            <div className="corr-panel-estados">
              {ESTADOS.map((e) => (
                <div key={e} className="corr-panel-est" data-testid={`panel-est-${estadoClase(e)}`}>
                  <span className={`corr-estado ${estadoClase(e)}`}>{e}</span>
                  <b>{panel.por[e]}</b>
                </div>
              ))}
            </div>
          </div>

          <h2 className="corr-h2">Bandeja de leads</h2>
          {leads.map((l) => (
            <button
              key={l.id}
              type="button"
              className={`corr-lead ${abierto === l.id ? 'on' : ''} ${destacado === l.id ? 'nuevo-demo' : ''}`}
              onClick={() => { abrir(l); if (destacado === l.id) setDestacado(null); }}
              data-testid={`corr-lead-${l.id}`}
            >
              <div className="corr-lead-top">
                <strong>{l.nombre}</strong>
                {destacado === l.id
                  ? <span className="corr-recien" data-testid="corr-recien">Recién llegado</span>
                  : <span className={`corr-estado ${estadoClase(l.estado)}`} data-testid={`corr-estado-${l.id}`}>{l.estado}</span>}
              </div>
              <span className="corr-lead-nec">{l.necesidad} · {(l.productos || []).join(', ') || '—'}</span>
              <span className="corr-lead-marca">{(MARCAS[l.marca] || marca).nombre}</span>
            </button>
          ))}
          <button type="button" className="corr-reset-bandeja" onClick={reiniciarBandeja} data-testid="corr-reset-bandeja">
            Reiniciar bandeja
          </button>
        </aside>

        {/* Detalle */}
        <main className="corr-detalle" data-testid="corr-detalle">
          {!lead ? (
            <div className="corr-vacio"><IconShield size={26} /><p>Elige un lead de la bandeja para trabajarlo.</p></div>
          ) : (
            <>
              <div className="corr-lead-head">
                <div>
                  <h2 className="corr-h2">{lead.nombre}</h2>
                  <p className="corr-meta">{lead.necesidad} · WhatsApp {lead.whatsapp || 'ND'} · consentimiento {lead.consent ? 'sí' : 'no'}</p>
                </div>
                {fase === 'idle' && (
                  <button type="button" className="corr-buscar" style={{ background: marca.color }} onClick={buscarComparar} data-testid="corr-buscar-comparar">
                    <IconSearch size={16} /> Buscar y comparar
                  </button>
                )}
              </div>

              <PerfilCaso perfil={lead.perfil} />

              {/* Agentes trabajando */}
              {fase !== 'idle' && (
                <div className="corr-agentes" data-testid="corr-agentes">
                  {AGENTES.map((a, i) => (
                    <div key={a.id} className={`corr-agente ${activo > i ? 'done' : activo === i ? 'run' : 'wait'}`} data-testid={`corr-agente-${a.id}`}>
                      <span className="corr-agente-ic">{activo > i ? <IconCheck size={15} /> : <span className="corr-dot" />}</span>
                      <div>
                        <strong>{a.nombre}</strong>
                        <span>{a.id === 'catalogo' && resultado ? `${resultado.cat.n} de ${CATALOGO_TOTAL} productos` : a.desc}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Resultado */}
              {fase === 'listo' && resultado && (
                <div className="corr-resultado" data-testid="corr-resultado">
                  <h3 className="corr-h3">Comparador — solo datos del catálogo</h3>
                  {(() => {
                    const showEdad = resultado.comp.some((r) => r.edadReal);
                    return (
                      <div className="corr-tabla-wrap" data-testid="corr-tabla-wrap">
                        <table className="corr-tabla" data-testid="corr-tabla">
                          <thead>
                            <tr>
                              <th>Producto</th><th>Prima</th><th>Deducible</th><th>Qué cubre</th><th>Exclusiones clave</th>
                              {showEdad ? <th>Edad máx. ingreso</th> : null}
                            </tr>
                          </thead>
                          <tbody>
                            {resultado.comp.map((r) => (
                              <tr key={r.id} data-testid={`corr-fila-${r.id}`}>
                                <td><b>{r.nombre}</b><span className="corr-cat">{r.categoria}</span></td>
                                <td>{r.prima}<span className="corr-origen">{r.origen}</span></td>
                                <td>{r.deducible}</td>
                                <td>{r.cubre}</td>
                                <td>{r.excluye}</td>
                                {showEdad ? <td>{r.edad}</td> : null}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        <div className="corr-tabla-swipe" data-testid="corr-tabla-swipe">Desliza para ver más →</div>
                      </div>
                    );
                  })()}

                  <h3 className="corr-h3">Alertas de cumplimiento</h3>
                  <ul className="corr-alertas" data-testid="corr-alertas">
                    {resultado.alertas.map((a, i) => (
                      <li key={i} className={a.tipo === 'Sin alertas' ? 'ok' : ''}><b>{a.tipo}:</b> {a.txt}</li>
                    ))}
                  </ul>

                  <div className="corr-ficha" data-testid="corr-ficha">
                    <h3 className="corr-h3">Ficha para la llamada</h3>
                    {resultado.ficha.resumen.map((r, i) => <p key={i} className="corr-ficha-linea">{r}</p>)}
                    {resultado.ficha.entendido.length > 0 && (
                      <p className="corr-ficha-sub">Ya entendió: {resultado.ficha.entendido.join(' · ')}</p>
                    )}
                    <p className="corr-ficha-sub">Preguntas sugeridas:</p>
                    <ol className="corr-preguntas">{resultado.ficha.preguntas.map((q, i) => <li key={i}>{q}</li>)}</ol>
                    <p className="corr-ficha-nota">La decisión y la recomendación son del corredor humano.</p>
                  </div>

                  <button type="button" className="corr-llamar" style={{ background: marca.color }} onClick={llamar} data-testid="corr-llamar">
                    <IconPhone size={16} /> Que el asistente llame al cliente
                  </button>
                  <button type="button" className="corr-pdf" onClick={() => pdfFichaLlamada(lead, marca, resultado)} data-testid="corr-exportar-pdf">
                    <IconDownload size={15} /> Descargar ficha (PDF)
                  </button>
                  <button type="button" className="corr-pdf" onClick={enviarWhatsApp} data-testid="corr-whatsapp">
                    <IconChat size={15} /> Enviar por WhatsApp
                  </button>
                  {lead.transcripcion && (
                    <div className="corr-transcript" data-testid="corr-transcript">
                      <strong>Transcripción de la última llamada</strong>
                      <pre>{lead.transcripcion}</pre>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </main>
      </div>

      {llamada && (
        <VozLlamada marca={marca} lead={llamada} onClose={cerrarLlamada} />
      )}
    </div>
  );
}
